<script setup lang="ts">
/* =============================================================
   TABS
   =============================================================
   A tab bar only -- the page renders the matching panel itself,
   keyed off the same value. That keeps panels as plain page
   markup (no slot-per-tab plumbing) and lets the page put the
   current tab in the URL.

   ARIA tablist with roving tabindex: Tab enters the bar on the
   selected tab, arrow keys move along it, Home/End jump. On a
   phone the bar scrolls sideways instead of wrapping.
   ============================================================= */

import { nextTick, useTemplateRef } from 'vue'

export interface TabItem {
  key: string
  label: string
  /** Small count after the label, e.g. number of rules. */
  count?: number | null
}

const props = defineProps<{ tabs: TabItem[]; modelValue: string; label: string }>()
const emit = defineEmits<{ 'update:modelValue': [key: string] }>()

const buttons = useTemplateRef<HTMLButtonElement[]>('buttons')

async function select(index: number): Promise<void> {
  const tab = props.tabs[index]
  if (!tab) return
  emit('update:modelValue', tab.key)
  await nextTick()
  buttons.value?.[index]?.focus()
}

function onKeydown(event: KeyboardEvent, index: number): void {
  const last = props.tabs.length - 1
  const next =
    event.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
    : event.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
    : event.key === 'Home' ? 0
    : event.key === 'End' ? last
    : null
  if (next === null) return
  event.preventDefault()
  void select(next)
}
</script>

<template>
  <div class="tabs" role="tablist" :aria-label="label">
    <button
      v-for="(tab, i) in tabs"
      :id="`tab-${tab.key}`"
      ref="buttons"
      :key="tab.key"
      type="button"
      role="tab"
      class="tab"
      :class="{ active: tab.key === modelValue }"
      :aria-selected="tab.key === modelValue"
      :aria-controls="`tabpanel-${tab.key}`"
      :tabindex="tab.key === modelValue ? 0 : -1"
      @click="select(i)"
      @keydown="onKeydown($event, i)"
    >
      {{ tab.label }}
      <span v-if="tab.count !== undefined && tab.count !== null" class="count">{{ tab.count }}</span>
    </button>
  </div>
</template>

<style scoped>
.tabs {
  display: flex;
  gap: var(--space-3xs);
  margin-bottom: var(--space-md);
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: var(--border-width) solid var(--color-border);
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  /* Tall enough for a thumb. */
  min-height: 2.75rem;
  padding-inline: var(--space-sm);
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  white-space: nowrap;
  color: var(--color-text-muted);
  background: none;
  border: 0;
  /* The underline sits on the bar's own border. */
  border-bottom: 2px solid transparent;
  margin-bottom: calc(var(--border-width) * -1);
  cursor: pointer;
  transition:
    color var(--dur-fast) var(--ease),
    border-color var(--dur-fast) var(--ease);
}

.tab:hover {
  color: var(--color-heading);
}

.tab:focus-visible {
  outline: none;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-focus);
}

.active,
.active:hover {
  color: var(--color-accent-hover);
  border-bottom-color: var(--color-accent);
}

.count {
  padding: 0 var(--space-3xs);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-full);
}

/* Four tabs fit a phone without scrolling if they give up some padding. */
@media (max-width: 40rem) {
  .tab {
    padding-inline: var(--space-2xs);
  }
}
</style>
